import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import * as THREE from "three";

export function loadObject(url: string): Promise<any | undefined> {
  const splitted = url.split(".");
  const format = splitted[splitted.length - 1];
  let loader = null;

  switch (format) {
    case "obj":
      loader = new OBJLoader();
      break;

    case "glb":
      loader = new GLTFLoader();
      break;
  }

  if (!loader) return Promise.reject("");
  return Promise.resolve(
    loader.load(
      url,
      (obj: any) => {
        switch (format) {
          case "obj":
            return Promise.resolve(obj);

          case "glb":
            return Promise.resolve(obj.scene);
        }
      },
      undefined,
      (err: any) => {
        return Promise.reject(err);
      }
    )
  );
}

type LightConfiguration = {
  count: number;
  intensity: number;
  radiusMultiplier: number;
};
export class RoomEnvironment {
  box: THREE.Box3 = null;
  lights: LightConfiguration = null;

  constructor(box: THREE.Box3, lights: LightConfiguration) {
    this.box = box;
    this.lights = lights;
  }

  build(): THREE.Scene {
    const scene = new THREE.Scene();

    const geometry = new THREE.BoxBufferGeometry();
    geometry.deleteAttribute("uv");

    const roomMaterial = new THREE.MeshStandardMaterial({
      side: THREE.BackSide,
    });

    const room = new THREE.Mesh(geometry, roomMaterial);
    room.position.set(-0.757, 13.219, 0.717);
    room.scale.set(31.713, 28.305, 28.591);
    scene.add(room);

    const sphere = new THREE.Sphere();
    const light = new THREE.DirectionalLight("white", 1.3);
    light.position.set(
      sphere.center.x,
      sphere.center.y + sphere.radius + 8,
      sphere.center.z
    );
    scene.add(light);

    this.box.getBoundingSphere(sphere);

    const positions = generateSpherePointsOnSphere(
      sphere,
      this.lights.count || 40,
      this.lights.radiusMultiplier || 20
    );
    for (let i = 0; i < positions.length; i++) {
      const l = new THREE.Mesh(
        geometry,
        createAreaLightMaterial(this.lights.intensity || 50)
      );
      const p = positions[i];
      l.position.set(p.x, p.y, p.z);
      scene.add(l);
    }

    function createAreaLightMaterial(intensity: number) {
      const material = new THREE.MeshBasicMaterial();
      material.color.setScalar(intensity);
      return material;
    }

    function generateSpherePointsOnSphere(
      sphere: THREE.Sphere,
      count: number,
      radiusMultiplier: number
    ) {
      const points = [];
      const radius = sphere.radius * radiusMultiplier;
      const inc = Math.PI * (3 - Math.sqrt(5));
      for (let i = 0; i < count; i++) {
        const y = (i * 2) / count - 1;
        const r = Math.sqrt(1 - y * y);
        const phi = i * inc;
        const x = Math.cos(phi) * r;
        const z = Math.sin(phi) * r;
        const point = new THREE.Vector3(x, y, z);
        point.multiplyScalar(radius);
        point.add(sphere.center);
        points.push(point);
      }
      return points;
    }

    return scene;
  }
}
